import { Box, Button, Stack, styled, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GET_ALL_NOTIFICATIONS } from '../../graphql/notifications';
import { client } from '../../service/apolloClient';
import { setAllNotificationsCleared } from '../../store/reducers/notificationsSlice';
import { formatDateWithTimeWithSeconds, getTimeAgo } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';

import DarkLoader from '../../assets/images/darkLoading.gif';
import LightLoader from '../../assets/images/lightLoading.gif';

export const LoaderUi = styled(Stack)(({ theme }) => ({
    backgroundImage: theme.palette.mode === 'dark' ? `url(${LightLoader})` : `url(${DarkLoader})`,
    width: theme.spacing(6.25),
    height: theme.spacing(6.25),
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    position: 'absolute',
    right: 0,
}));
interface IProps {
    onClose: () => void;
}
const NotificationOverlay: React.FC<IProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [list, setNotificationsList] = useState([]);
    const [showLoading, setShowLoading] = useState(false);
    const dispatch = useDispatch();
    const getAllNotifications = async () => {
        try {
            setShowLoading(true);
            const { data } = await client.query({
                query: GET_ALL_NOTIFICATIONS,
                variables: {
                    limit: 3,
                    page: 1,
                },
                fetchPolicy: 'network-only',
            });
            if (data && data?.getNotifications) {
                const listData = data?.getNotifications?.data;
                const modifiedListData = listData.map((item: any) =>
                    item?.type === 8 || item?.type === 7
                        ? {
                              ...item,
                              message: `${item?.message} at ${formatDateWithTimeWithSeconds(item?.meta_data?.sensor_timestamp)}`,
                          }
                        : item,
                );
                setNotificationsList(modifiedListData);
            }
        } catch (error) {
            console.error('Error fetching user notifications:', error);
        } finally {
            setShowLoading(false);
        }
    };
    React.useEffect(() => {
        getAllNotifications();
    }, []);
    const navigateToViewAllNotifications = () => {
        dispatch(setAllNotificationsCleared(false));
        navigate(NavigationConfig.Notifications);
        onClose();
    };
    const handleNotificationClick = (item: any) => {
        const type = item?.type;
        switch (type) {
            case 1:
            case 2:
                navigate(NavigationConfig.ProfileSetting);
                break;
            case 7:
            case 8:
                navigate(NavigationConfig.Notifications);
                break;
            default:
        }
        onClose();
    };
    return (
        <Stack className="container" p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" position="relative">
                <Typography variant="h6" fontWeight={700} fontFamily="good_sansregular">
                    {t('notifications')}
                </Typography>
                {showLoading && <LoaderUi />}
            </Stack>
            <Stack my={2} rowGap={1.5}>
                {list?.map((item: any) => (
                    <Box p={2} className="cards" onClick={() => handleNotificationClick(item)} key={item?.id}>
                        <Stack direction="row" justifyContent="space-between" pb={0.76} gap={1}>
                            <Typography variant="h6" fontWeight={700}>
                                {item.message}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                whiteSpace="nowrap"
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                {getTimeAgo(item?.createdAt)}
                                {item?.read_status === 0 && <Box className="unread" />}
                            </Typography>
                        </Stack>
                    </Box>
                ))}
                {showLoading ? (
                    <Box p={2} className="cards" textAlign="center">
                        <Typography className="subText" variant="subtitle1" fontFamily="good_sansregular">
                            Please wait...
                        </Typography>
                    </Box>
                ) : (
                    list?.length === 0 && (
                        <Box p={2} className="cards">
                            <Typography className="subText" variant="subtitle1">
                                {t('no_notifications_text')}
                            </Typography>
                        </Box>
                    )
                )}
            </Stack>
            {list?.length > 0 && (
                <Button
                    onClick={navigateToViewAllNotifications}
                    sx={{ width: 'fit-content', margin: 'auto' }}
                    variant="text"
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                >
                    <Typography variant="subtitle1" letterSpacing={0.4} fontFamily="good_sansregular">
                        {t('view_all')}
                    </Typography>
                </Button>
            )}
        </Stack>
    );
};
export { NotificationOverlay };
